// ModernWorkspacePresetPicker — Modern ヘッダ帯のワークスペース preset ピッカー.
//
// 背景 (docs/_wip/modern-layout.md §9.10、consult 2026-06-16 第2スライス):
//   - 第1スライスの単独「UI左右入替」トグル (1 ボタン) を **廃し**、curated な Tier1
//     preset (標準 / 左右入替 / お絵描き集中 / チャット重視) を選ぶ
//     ピッカーに置換する (§9.10 決定3 / 4「preset ピッカーに吸収されれば独立トグルが
//     消える」)。verb 名「UI左右入替」は purpose 名 `左右入替` preset として残る。
//   - 置き場所は §9.4.1①「両レイアウト共通 chrome の設定」流儀 = 共有 RoomHeader の
//     personal-scope 相当 (= 自分の端末だけに効く)。RoomHeader の `kind:'custom'` slot へ。
//   - 軸を混ぜない (§9.10): これは **Modern UI 内の配置**で、Classic ⇄ Modern 切替
//     (LayoutToggle) とは別軸・別 UI。一列に並べない。
//
// 実装は素の `<select>` (= 初心者に馴染む・キーボード/スクリーンリーダ対応が標準で付く・
// 軽量)。`` ` `` キーの cycle (§9.10 決定4) と同じ 1 本の preset state を読み書きする。
//
// 第3スライス (consult 2026-06-14 決定2「手動編集 = 直接編集」): 手動編集 (パネル表示
// トグル / ドック幅リサイズ) が入ると live state は「カスタム」になり、名前付き preset から
// 乖離する。そのとき **ピッカーに動的な「カスタム」option を出して選択中に据える**
// (= 編集中であることを表現)。**名前付き preset を選び直すと preset の state で live を
// 置換し手動編集を破棄する** (= onChange は WorkspacePresetId のみを返す。「カスタム」は
// 選べない表示専用 option)。

import { CUSTOM_PICKER_VALUE, type WorkspacePickerValue } from './modernWorkspaceLive.js';
import type { WorkspacePresetId, WorkspacePresetMeta } from './workspaceLayout.js';

export interface ModernWorkspacePresetPickerProps {
  /** 選択肢 (= WORKSPACE_PRESETS、宣言順 = ピッカー表示順)。 */
  readonly presets: ReadonlyArray<WorkspacePresetMeta>;
  /**
   * 現在のピッカー値。preset モードなら preset id、手動編集済みなら `'custom'`
   * (= 動的な「カスタム」option を選択中に出す)。
   */
  readonly value: WorkspacePickerValue;
  /**
   * preset 選択変更 (= 名前付き preset を選び直す)。手動編集は破棄され preset へ戻る。
   * 「カスタム」は表示専用なので onChange では返らない (= WorkspacePresetId のみ)。
   */
  readonly onChange: (next: WorkspacePresetId) => void;
}

/**
 * ワークスペース preset を選ぶ帯内ピッカー。purpose 名を並べる (§9.10 命名節)。
 * RoomHeader のボタン群と高さ・font を揃えた compact な select。
 */
export const ModernWorkspacePresetPicker = ({
  presets,
  value,
  onChange,
}: ModernWorkspacePresetPickerProps): React.ReactNode => {
  const isCustomValue = value === CUSTOM_PICKER_VALUE;
  const current = presets.find((p) => p.id === value);
  return (
    <label className="flex shrink-0 items-center gap-1">
      {/* ラベルは視覚的にも出す (= 何のセレクタか自明に)。狭幅で邪魔なら将来 sr-only 化検討。 */}
      <span className="select-none whitespace-nowrap text-[11px] font-bold text-white/70">
        配置
      </span>
      <select
        aria-label="ワークスペースの配置 (パネルの並び・表示)"
        title={
          isCustomValue
            ? 'パネルを手動で調整した配置 (preset を選ぶと元に戻ります)'
            : (current?.description ?? 'ワークスペースの配置を切り替える')
        }
        value={value}
        data-manual="layout.workspace"
        onChange={(e) => onChange(e.target.value as WorkspacePresetId)}
        className="shrink-0 cursor-pointer rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-2 py-0.5 text-[11px] font-bold text-white hover:bg-[var(--color-bg)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
      >
        {/* 手動編集中だけ動的に「カスタム」option を出して選択中に据える (= 編集中表現)。
            表示専用 (= 選んでも no-op になる値) だが、select の value が presets に無いと
            React が警告するため、custom のときだけ option を出す。 */}
        {isCustomValue && (
          <option value={CUSTOM_PICKER_VALUE} title="パネルを手動で調整した配置">
            カスタム
          </option>
        )}
        {presets.map((p) => (
          <option key={p.id} value={p.id} title={p.description}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );
};
